/* Read-only help for the existing Studio. It never modifies a design or offer. */
(function (root, factory) {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.TodayMerchTechniqueHelp = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, root => {
  'use strict';
  let sequence = 0;
  const bounded = value => typeof value === 'string' ? value.slice(0, 300) : '';
  const positive = value => typeof value === 'number' && Number.isFinite(value) && value > 0;
  const contextKeys = ['productId', 'variantId', 'supplierId', 'techniqueId', 'supportId', 'positionId', 'positionLabel',
    'originalDigest', 'effectiveDigest', 'designRevision', 'rulesRevision', 'placementRevision', 'applicationId',
    'contextKey', 'revision', 'sku', 'productCode', 'methodCode'];
  function copyContext(input = {}) {
    const result = {};
    for (const key of contextKeys) result[key] = bounded(Number.isSafeInteger(input[key]) ? String(input[key]) : input[key]);
    const d = input.dimensions;
    result.dimensions = d && positive(d.widthMm) && positive(d.heightMm)
      ? { widthMm: d.widthMm, heightMm: d.heightMm } : null;
    return result;
  }
  function safeImage(value) {
    if (typeof value !== 'string' || /[\u0000-\u001f]/u.test(value)) return null;
    try {
      const url = new URL(value, root.location?.href || 'https://invalid.local/');
      return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
    } catch { return null; }
  }
  function create({ onAssistance } = {}) {
    const doc = root.document;
    if (!doc?.body) throw new TypeError('L’aide nécessite un document ouvert.');
    const id = `personalization-technique-help-${++sequence}`;
    const el = (tag, className, text) => {
      const node = doc.createElement(tag);
      if (className) node.className = className;
      if (text) node.textContent = text;
      return node;
    };
    const cls = part => `personalization-technique-help__${part}`;
    let destroyed = false;
    let returnFocus = null;
    let state = { techniques: [], selectedId: '', context: {}, imageUrls: {} };
    let firstId = '', secondId = '', comparing = false;
    const dialog = el('dialog', 'personalization-technique-help');
    dialog.setAttribute('aria-labelledby', `${id}-title`);
    dialog.dataset.personalizationTechniqueHelp = '';
    const header = el('header', cls('header'));
    const title = el('h2', '', 'Comprendre les techniques'); title.id = `${id}-title`;
    const close = el('button', cls('close'), 'Fermer'); close.type = 'button';
    close.setAttribute('aria-label', 'Fermer les explications');
    header.append(title, close);
    const content = el('div', cls('content'));
    const intro = el('p', cls('intro'), 'Comparez l’aspect, le toucher et la préparation du visuel pour choisir le rendu qui vous convient.');
    intro.id = `${id}-intro`; dialog.setAttribute('aria-describedby', intro.id);
    const controls = el('div', cls('controls'));
    const selectGroup = (suffix, label) => {
      const wrap = el('div', cls('select-group'));
      const caption = el('label', '', label); caption.htmlFor = `${id}-${suffix}`;
      const select = el('select'); select.id = caption.htmlFor;
      wrap.append(caption, select); controls.append(wrap); return { wrap, select };
    };
    const first = selectGroup('first', 'Technique à découvrir');
    const second = selectGroup('second', 'Comparer avec');
    const compareLabel = el('label', cls('compare'));
    const compare = el('input'); compare.type = 'checkbox'; compare.id = `${id}-compare`;
    compareLabel.append(compare, doc.createTextNode('Comparer deux techniques'));
    const context = el('p', cls('context')); context.dataset.helpContext = '';
    const live = el('p', cls('live')); live.setAttribute('role', 'status'); live.setAttribute('aria-atomic', 'true');
    const levels = el('section', cls('levels'));
    levels.setAttribute('aria-label', 'Comprendre les trois niveaux de preuve');
    for (const [label, text] of [
      ['Votre aperçu', 'Placement et proportions de votre visuel dans le Studio. L’effet reste indicatif.'],
      ['Exemple de finition', 'Photo indépendante d’un procédé sur un support identifié, distincte de votre fichier.'],
      ['Contrôle technique', 'Vérification du fichier à la taille retenue. Ni l’aperçu ni une photo ne valident la fabrication.']
    ]) {
      const item = el('div'); item.append(el('h3', '', label), el('p', '', text)); levels.append(item);
    }
    const columns = el('div', cls('columns')); columns.dataset.helpColumns = '';
    const assistance = el('aside', cls('assistance'));
    assistance.append(el('h3', '', 'Un doute sur votre fichier ?'), el('p', '',
      'Petits textes, traits fins, dégradés ou couleur essentielle : l’équipe peut vous aider à examiner les adaptations. Gardez votre original ; vous n’avez pas à préparer seul un fichier de fabrication.'));
    let ask = null;
    if (typeof onAssistance === 'function') {
      ask = el('button', cls('assist-button'), 'Demander conseil'); ask.type = 'button'; assistance.append(ask);
    }
    content.append(intro, compareLabel, controls, context, live, levels, columns, assistance);
    const footer = el('footer', cls('footer'));
    footer.append(el('span', '', 'Vos choix et votre fichier restent dans le Studio.'));
    const done = el('button', cls('done'), 'Revenir au Studio'); done.type = 'button'; footer.append(done);
    dialog.append(header, content, footer); doc.body.append(dialog);

    const model = technique => {
      const context = { ...state.context,
        supplierId: technique.supplierId || state.context.supplierId,
        techniqueId: technique.techniqueId || (technique.documentationOnly ? '' : technique.id),
        processId: technique.processId, supportId: technique.supportId || state.context.supportId,
        applicationId: technique.applicationId || state.context.applicationId
      };
      return root.TodayMerchMarkingTechniqueGuidance?.help?.(context)
        || root.TodayMerchTechniqueHelpContent?.resolve?.(context) || null;
    };

    function rebuildOptions(select, chosen, excluded) {
      select.replaceChildren();
      for (const technique of state.techniques) {
        const data = model(technique);
        const option = el('option', '', data?.title || 'Technique à préciser'); option.value = technique.id;
        option.disabled = technique.id === excluded;
        select.append(option);
      }
      select.value = chosen;
    }
    function section(article, heading, text) {
      const item = el('section', cls('section')); item.append(el('h4', '', heading), el('p', '', text)); article.append(item);
    }
    function example(article, technique, data) {
      const proof = root.TodayMerchMarkingTechniqueVisuals?.resolveExample?.({
        supplierId: technique.supplierId || state.context.supplierId,
        techniqueCode: technique.techniqueId || (technique.documentationOnly ? '' : technique.id),
        visualId: data.visualId, supportId: technique.supportId || state.context.supportId,
        positionId: state.context.positionId
      });
      const block = el('figure', cls('example'));
      block.append(el('h4', '', 'Exemple de finition'));
      const source = proof?.kind === 'finish-example' ? safeImage(state.imageUrls[proof.imageFile]) : null;
      if (source) {
        const img = el('img'); img.alt = bounded(proof.alt); img.loading = 'lazy'; img.decoding = 'async'; img.src = source;
        img.addEventListener('error', () => {
          img.remove(); block.querySelector('figcaption').textContent = 'L’exemple est indisponible. Consultez les explications du procédé ; aucune autre photo ne le remplace.';
        }, { once: true });
        block.append(img);
      }
      const caption = source ? bounded(proof.caption)
        : 'Aucun exemple vérifié et autorisé pour ce procédé sur ce support. Les explications restent disponibles.';
      block.append(el('figcaption', '', caption)); article.append(block);
    }
    function renderArticle(technique) {
      const data = model(technique);
      const article = el('article', cls('technique')); article.dataset.helpTechnique = technique.id;
      article.append(el('h3', '', data?.title || 'Technique à préciser'));
      if (!data) {
        article.append(el('p', '', 'Les explications sont momentanément indisponibles. L’équipe peut préciser ce procédé avec vous.'));
        return article;
      }
      if (data.stage === 'preparatory' || data.mappingStatus !== 'exact-code') {
        article.append(el('p', cls('preparatory'), 'Guide préparatoire · disponibilité à confirmer pour ce produit'));
      }
      article.append(el('p', cls('benefit'), data.benefit));
      section(article, 'Le rendu', data.detail);
      section(article, 'Le compromis', data.tradeoff);
      section(article, 'Votre fichier', data.prepare);
      section(article, 'Sur ce support', data.supportNote);
      section(article, 'Les points à vérifier', data.limits);
      if (data.application) section(article, data.application.title, `${data.application.detail} ${data.application.requires}`);
      example(article, technique, data);
      section(article, 'Ce que permet l’aperçu', data.preview);
      return article;
    }
    function render() {
      if (destroyed) return;
      const focused = doc.activeElement;
      const available = state.techniques.map(item => item.id);
      if (!available.includes(firstId)) firstId = available.includes(state.selectedId) ? state.selectedId : available[0] || '';
      if (!available.includes(secondId) || secondId === firstId) secondId = available.find(value => value !== firstId) || '';
      if (available.length < 2) comparing = false;
      compare.checked = comparing; compare.disabled = available.length < 2;
      first.select.disabled = available.length === 0; second.wrap.hidden = !comparing;
      rebuildOptions(first.select, firstId);
      rebuildOptions(second.select, secondId, firstId);
      const d = state.context.dimensions;
      const format = value => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(value);
      const size = d ? `${format(d.widthMm)} × ${format(d.heightMm)} mm` : 'dimensions à confirmer';
      const position = bounded(state.context.positionLabel);
      context.textContent = `${comparing ? 'Même contexte de comparaison' : 'Votre configuration'}${position ? ` · ${position}` : ''} · ${size}.`;
      if (comparing) context.textContent += state.context.effectiveDigest
        ? ' Le fichier effectif reste le même ; cette comparaison explique les procédés sans transformer le dessin.'
        : ' Aucun fichier n’est lié à cette aide : la comparaison décrit seulement les procédés.';
      columns.dataset.comparing = String(comparing); columns.replaceChildren();
      const techniques = [firstId, ...(comparing ? [secondId] : [])].map(value => state.techniques.find(item => item.id === value)).filter(Boolean);
      for (const technique of techniques) columns.append(renderArticle(technique));
      if (!techniques.length) columns.append(el('p', cls('empty'), 'Aucune technique n’est proposée dans ce contexte. L’équipe peut étudier votre projet.'));
      live.textContent = comparing ? 'Deux techniques comparées. Votre sélection dans le Studio ne change pas.' : '';
      if (dialog.open && dialog.contains(focused) && (focused.disabled || focused.closest('[hidden]') || !focused.isConnected)) {
        (first.select.disabled ? close : first.select).focus();
      }
    }
    function update(next = {}) {
      if (destroyed) return;
      if (Object.prototype.hasOwnProperty.call(next, 'context')) state.context = copyContext(next.context || {});
      if (Object.prototype.hasOwnProperty.call(next, 'selectedId')) state.selectedId = bounded(next.selectedId);
      if (Object.prototype.hasOwnProperty.call(next, 'imageUrls')) {
        state.imageUrls = Object.fromEntries(Object.entries(next.imageUrls || {}).filter(([key, value]) => bounded(key) && typeof value === 'string'));
      }
      if (Array.isArray(next.techniques)) {
        const seen = new Set();
        state.techniques = next.techniques.filter(item => item && bounded(item.id) && !seen.has(item.id) && seen.add(item.id)).slice(0, 50)
          .map(item => ({ ...Object.fromEntries(['id', 'techniqueId', 'supplierId', 'processId', 'supportId', 'applicationId'].map(key => [key, bounded(item[key])])), documentationOnly: item.documentationOnly === true }));
      }
      render();
    }
    function closeHelp() { if (!destroyed && dialog.open) dialog.close(); }
    function restoreFocus() {
      const focus = returnFocus; returnFocus = null;
      if (focus?.isConnected && typeof focus.focus === 'function') focus.focus();
    }
    close.addEventListener('click', closeHelp); done.addEventListener('click', closeHelp);
    dialog.addEventListener('close', restoreFocus);
    compare.addEventListener('change', () => { comparing = compare.checked; render(); });
    first.select.addEventListener('change', () => { firstId = first.select.value; render(); });
    second.select.addEventListener('change', () => { secondId = second.select.value; render(); });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const nodes = [...dialog.querySelectorAll('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href]')]
        .filter(node => !node.closest('[hidden]') && node.getClientRects().length);
      const firstNode = nodes[0], lastNode = nodes[nodes.length - 1];
      if (event.shiftKey && doc.activeElement === firstNode) { event.preventDefault(); lastNode.focus(); }
      else if (!event.shiftKey && doc.activeElement === lastNode) { event.preventDefault(); firstNode.focus(); }
    });
    ask?.addEventListener('click', () => {
      const snapshot = { context: copyContext(state.context), selectedId: state.selectedId,
        techniqueIds: [firstId, ...(comparing ? [secondId] : [])],
        reason: 'technique-advice', source: 'technique-help' };
      Object.freeze(snapshot.context.dimensions); Object.freeze(snapshot.context); Object.freeze(snapshot.techniqueIds); Object.freeze(snapshot);
      dialog.dispatchEvent(new root.CustomEvent('personalization-technique-help:assistance', { detail: snapshot, bubbles: true }));
      onAssistance(snapshot);
    });
    return Object.freeze({
      open(next = {}) {
        if (destroyed || typeof dialog.showModal !== 'function') return false;
        firstId = bounded(next.selectedId); comparing = false; update(next);
        if (!dialog.open) { returnFocus = next.returnFocus || doc.activeElement; dialog.showModal(); close.focus(); }
        return true;
      },
      update, close: closeHelp,
      destroy() {
        if (destroyed) return;
        closeHelp(); restoreFocus(); destroyed = true; dialog.remove();
        state = { techniques: [], context: {}, imageUrls: {} };
      }
    });
  }
  return Object.freeze({ create });
});
